import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
    
    if (!userDoc.exists()) {
      // Create user document if it doesn't exist
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: userCredential.user.email,
        username: email.split('@')[0],
        role: 'user',
        permissions: { inventory: false, sales: false, reports: false, settings: false },
        createdAt: new Date()
      });
      setUserPermissions({ inventory: false, sales: false, reports: false, settings: false });
    } else {
      setUserPermissions(userDoc.data().permissions);
    }
    
    return { ...userCredential.user, ...(userDoc.exists() ? userDoc.data() : {}) };
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    
    // Check if user exists in Firestore (pre-approved)
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
    
    if (!userDoc.exists()) {
      // Sign out unauthorized user
      await signOut(auth);
      throw new Error('Access denied. Contact admin to get access.');
    }
    
    const userData = userDoc.data();
    setUserPermissions(userData.permissions || {});
    return { ...userCredential.user, ...userData };
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCurrentUser({ ...user, ...userData });
          setUserPermissions(userData.permissions || {});
        } else {
          setCurrentUser(user);
          setUserPermissions({});
        }
      } else {
        setCurrentUser(null);
        setUserPermissions(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = () => signOut(auth);

  const value = {
    currentUser,
    userPermissions,
    login,
    loginWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};